import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Layers, BookOpen, MessageSquare, Puzzle,
  ArrowRight, ArrowLeft, Loader2, Search,
  ChevronDown, ChevronRight, Check, Minus, Copy,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Type-scoped metadata used across screens.
const TYPE_META = {
  track:   { label: "Track",             pluralLabel: "Tracks",             icon: Layers,        libraryRoute: "/admin/tracks" },
  lexp:    { label: "LEXP",              pluralLabel: "LEXPs",              icon: BookOpen,      libraryRoute: "/admin/learning-experiences" },
  survey:  { label: "Survey",            pluralLabel: "Surveys",            icon: MessageSquare, libraryRoute: "/admin/surveys" },
  item:    { label: "Question Bank item",pluralLabel: "Question Bank items",icon: Puzzle,        libraryRoute: "/admin/items" },
};

const KIND_LABEL = { track: "Track", lexp: "LEXP", survey: "Survey", assessment: "Assessment", item: "Item" };

// Light-theme palette (matches CurriculumManager existing modals).
const COLORS = {
  bg: "#ffffff",
  border: "#e2e6ef",
  borderMuted: "#eef1f5",
  title: "#062546",
  body: "#5a6577",
  subtle: "#8896a7",
  surface: "#f7f9fc",
  hover: "#eef3fb",
  primary: "var(--primary-color, #4A90D9)",
};

// --- helpers for tree manipulation ------------------------------------------
function forEachNode(node, cb, depth = 0) {
  cb(node, depth);
  (node.children || []).forEach((c) => forEachNode(c, cb, depth + 1));
}
function allKeys(node) {
  const s = new Set();
  forEachNode(node, (n) => s.add(`${n.kind}:${n.id}`));
  return s;
}
function keysForSubtree(node) {
  const s = new Set();
  forEachNode(node, (n) => s.add(`${n.kind}:${n.id}`));
  return s;
}
function countByKind(tree, checked) {
  const total = { track: 0, lexp: 0, survey: 0, assessment: 0, item: 0 };
  const sel = { track: 0, lexp: 0, survey: 0, assessment: 0, item: 0 };
  if (!tree) return { total, sel };
  forEachNode(tree, (n) => {
    total[n.kind] = (total[n.kind] || 0) + 1;
    if (checked.has(`${n.kind}:${n.id}`)) sel[n.kind] = (sel[n.kind] || 0) + 1;
  });
  return { total, sel };
}

// ============================================================================
// DuplicationWizard
// ============================================================================
export default function DuplicationWizard({ open, onClose }) {
  const navigate = useNavigate();
  const [screen, setScreen] = useState(1);
  const [type, setType] = useState(null);

  // Screen 2
  const [sources, setSources] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceSearch, setSourceSearch] = useState("");
  const [sourceStatus, setSourceStatus] = useState("all");
  const [selectedSource, setSelectedSource] = useState(null);

  // Screen 3
  const [tree, setTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());

  // Screen 4
  const [newTitle, setNewTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setScreen(1); setType(null);
    setSources([]); setSourceSearch(""); setSourceStatus("all"); setSelectedSource(null);
    setTree(null); setChecked(new Set()); setExpanded(new Set());
    setNewTitle(""); setSubmitting(false);
  }, []);

  const handleClose = () => { reset(); if (onClose) onClose(); };

  // Screen 2: fetch sources
  useEffect(() => {
    if (screen !== 2 || !type) return;
    setSourceLoading(true);
    const params = new URLSearchParams();
    if (sourceSearch.trim()) params.set("q", sourceSearch.trim());
    if (sourceStatus !== "all") params.set("status", sourceStatus);
    axios.get(`${API}/duplicate/sources/${type}?${params.toString()}`, { withCredentials: true })
      .then(({ data }) => setSources(data || []))
      .catch(() => toast.error("Failed to load sources"))
      .finally(() => setSourceLoading(false));
  }, [screen, type, sourceSearch, sourceStatus]);

  // Screen 3: fetch tree when entering
  const loadTree = useCallback(async () => {
    if (!type || !selectedSource) return null;
    setTreeLoading(true);
    try {
      const { data } = await axios.get(`${API}/duplicate/tree/${type}/${selectedSource.id}`, { withCredentials: true });
      setTree(data);
      const ck = allKeys(data);
      setChecked(ck);
      const ex = new Set();
      forEachNode(data, (n) => { if ((n.children || []).length) ex.add(`${n.kind}:${n.id}`); });
      setExpanded(ex);
      return data;
    } catch (_err) {
      toast.error("Failed to load tree"); return null;
    } finally {
      setTreeLoading(false);
    }
  }, [type, selectedSource]);

  // Screen transitions ------------------------------------------------------
  const goScreen2 = () => setScreen(2);
  const goScreen3 = async () => {
    if (type === "item") {
      setNewTitle(`${selectedSource.title} (Copy)`);
      setScreen(4);
      return;
    }
    const t = await loadTree();
    if (t) setScreen(3);
  };
  const goScreen4 = () => {
    if (selectedSource) setNewTitle(`${selectedSource.title} (Copy)`);
    setScreen(4);
  };

  // Screen 3 actions --------------------------------------------------------
  const toggleNode = (node) => {
    if (node.kind === tree.kind) return;
    const keys = keysForSubtree(node);
    const isChecked = checked.has(`${node.kind}:${node.id}`);
    const next = new Set(checked);
    if (isChecked) { for (const k of keys) next.delete(k); }
    else { for (const k of keys) next.add(k); }
    next.add(`${tree.kind}:${tree.id}`);
    setChecked(next);
  };

  const isIndeterminate = (node) => {
    if (!(node.children || []).length) return false;
    let checkedCount = 0; let total = 0;
    for (const c of node.children || []) {
      forEachNode(c, (n) => {
        total += 1;
        if (checked.has(`${n.kind}:${n.id}`)) checkedCount += 1;
      });
    }
    return checkedCount > 0 && checkedCount < total;
  };

  const toggleExpand = (node) => {
    const k = `${node.kind}:${node.id}`;
    const next = new Set(expanded);
    if (next.has(k)) next.delete(k); else next.add(k);
    setExpanded(next);
  };

  const selectAllShortcut = () => { if (tree) setChecked(allKeys(tree)); goScreen4(); };
  const parentOnlyShortcut = () => { if (tree) setChecked(new Set([`${tree.kind}:${tree.id}`])); goScreen4(); };

  // Summary line ------------------------------------------------------------
  const summary = useMemo(() => {
    if (!tree) return [];
    const { total, sel } = countByKind(tree, checked);
    const lines = [];
    ["track", "lexp", "survey", "assessment", "item"].forEach((k) => {
      if ((total[k] || 0) > 0) {
        const label = k === "item" ? "item" : KIND_LABEL[k];
        lines.push(`${sel[k] || 0} of ${total[k]} ${label}${(total[k] !== 1 ? "s" : "")}`);
      }
    });
    return lines;
  }, [tree, checked]);

  const summaryCounts = useMemo(() => {
    if (!tree) return { list: [] };
    const { sel } = countByKind(tree, checked);
    const list = [];
    ["track", "lexp", "survey", "assessment", "item"].forEach((k) => {
      const n = sel[k] || 0;
      if (n > 0) {
        const label = k === "item" ? "item" : KIND_LABEL[k];
        list.push(`${n} ${label}${n !== 1 ? "s" : ""}`);
      }
    });
    return { list };
  }, [tree, checked]);

  // Submit ------------------------------------------------------------------
  const handleDuplicate = async () => {
    if (!type || !selectedSource) return;
    const title = (newTitle || "").trim();
    if (!title) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const payload = { type, source_id: selectedSource.id, new_title: title };
      if (type !== "item") payload.selection = Array.from(checked);
      const { data } = await axios.post(`${API}/duplicate`, payload, { withCredentials: true });
      toast.success(`${TYPE_META[type].label} duplicated as "${data.title}"`, {
        action: {
          label: "View in Library",
          onClick: () => navigate(TYPE_META[type].libraryRoute),
        },
      });
      handleClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Duplication failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Render ------------------------------------------------------------------
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] p-0 flex flex-col overflow-hidden"
        style={{ backgroundColor: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 16 }}
        data-testid="duplication-wizard"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0" style={{ borderBottom: `1px solid ${COLORS.borderMuted}` }}>
          <DialogTitle className="font-heading font-bold text-xl flex items-center gap-2" style={{ color: COLORS.title }}>
            <Copy className="h-4 w-4" style={{ color: COLORS.primary }} />
            Duplication Wizard
          </DialogTitle>
          <DialogDescription className="font-body text-xs" style={{ color: COLORS.subtle }}>
            Step {screen} of 4 {type ? `· ${TYPE_META[type].label}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {screen === 1 && <Screen1 type={type} setType={setType} />}
          {screen === 2 && (
            <Screen2
              type={type}
              sources={sources}
              loading={sourceLoading}
              search={sourceSearch}
              setSearch={setSourceSearch}
              statusFilter={sourceStatus}
              setStatusFilter={setSourceStatus}
              selected={selectedSource}
              setSelected={setSelectedSource}
            />
          )}
          {screen === 3 && (
            <Screen3
              tree={tree}
              loading={treeLoading}
              type={type}
              checked={checked}
              expanded={expanded}
              toggleNode={toggleNode}
              toggleExpand={toggleExpand}
              isIndeterminate={isIndeterminate}
              selectAll={selectAllShortcut}
              parentOnly={parentOnlyShortcut}
              summaryLines={summary}
            />
          )}
          {screen === 4 && (
            <Screen4
              type={type}
              source={selectedSource}
              counts={summaryCounts.list}
              newTitle={newTitle}
              setNewTitle={setNewTitle}
            />
          )}
        </div>

        <div className="flex justify-between items-center px-6 py-3 shrink-0" style={{ borderTop: `1px solid ${COLORS.borderMuted}`, backgroundColor: COLORS.bg }}>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="font-body"
            style={{ color: COLORS.body }}
            data-testid="dup-cancel"
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {screen > 1 && (
              <Button
                variant="outline"
                onClick={() => { if (screen === 4 && type === "item") setScreen(2); else setScreen(screen - 1); }}
                className="h-9 font-body"
                style={{ borderColor: COLORS.border, color: COLORS.body, backgroundColor: "#fff" }}
                data-testid="dup-back"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
            )}
            {screen === 1 && (
              <Button onClick={goScreen2} disabled={!type} className="h-9 text-white font-body" style={{ backgroundColor: COLORS.primary }} data-testid="dup-next-1">
                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {screen === 2 && (
              <Button onClick={goScreen3} disabled={!selectedSource} className="h-9 text-white font-body" style={{ backgroundColor: COLORS.primary }} data-testid="dup-next-2">
                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {screen === 3 && (
              <Button onClick={goScreen4} className="h-9 text-white font-body" style={{ backgroundColor: COLORS.primary }} data-testid="dup-next-3">
                Next <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            )}
            {screen === 4 && (
              <Button onClick={handleDuplicate} disabled={submitting || !newTitle.trim()} className="h-9 text-white font-body" style={{ backgroundColor: COLORS.primary }} data-testid="dup-submit">
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />} Duplicate
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Screen 1
// ---------------------------------------------------------------------------
function Screen1({ type, setType }) {
  return (
    <div className="space-y-4" data-testid="dup-screen-1">
      <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>What do you want to duplicate?</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.entries(TYPE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const active = type === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setType(key)}
              data-testid={`dup-type-${key}`}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors"
              style={{
                backgroundColor: active ? COLORS.hover : "#fff",
                border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
              }}
            >
              <Icon className="h-4 w-4 shrink-0" style={{ color: active ? COLORS.primary : COLORS.subtle }} />
              <span
                className="text-sm font-body"
                style={{ color: active ? COLORS.title : COLORS.body, fontWeight: active ? 600 : 400 }}
              >
                {meta.label}
              </span>
              {active && <Check className="h-3.5 w-3.5 ml-auto" style={{ color: COLORS.primary }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen 2
// ---------------------------------------------------------------------------
function Screen2({ type, sources, loading, search, setSearch, statusFilter, setStatusFilter, selected, setSelected }) {
  const hasStatusFilter = type !== "item";
  return (
    <div className="space-y-4" data-testid="dup-screen-2">
      <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>
        Select a {TYPE_META[type].label} to duplicate
      </h3>

      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: COLORS.subtle }} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${TYPE_META[type].pluralLabel}…`}
            data-testid="dup-search"
            className="pl-8 h-9 text-sm font-body"
            style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
          />
        </div>
        {hasStatusFilter && (
          <div className="flex gap-2">
            {["all", "draft", "published", "archived"].map((s) => {
              const active = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`dup-status-chip-${s}`}
                  className="text-[11px] font-body px-2.5 py-1 rounded-full transition-colors"
                  style={{
                    backgroundColor: active ? `${COLORS.primary}15` : "#fff",
                    border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                    color: active ? COLORS.primary : COLORS.body,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div
        className="rounded-lg max-h-[45vh] overflow-y-auto"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}
      >
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: COLORS.subtle }} /></div>
        ) : sources.length === 0 ? (
          <div className="text-center py-8 text-sm font-body" style={{ color: COLORS.subtle }}>
            No {TYPE_META[type].pluralLabel.toLowerCase()} found.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: COLORS.borderMuted }}>
            {sources.map((s) => {
              const active = selected?.id === s.id;
              const isArchived = s.status === "archived";
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSelected(s)}
                  data-testid={`dup-source-${s.id}`}
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors"
                  style={{ backgroundColor: active ? `${COLORS.primary}0D` : "transparent" }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = COLORS.hover; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  <div
                    className="mt-0.5 h-4 w-4 rounded-full shrink-0 flex items-center justify-center"
                    style={{
                      border: `2px solid ${active ? COLORS.primary : COLORS.subtle}`,
                      backgroundColor: active ? COLORS.primary : "transparent",
                    }}
                  >
                    {active && <div className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#fff" }} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-sm font-body truncate"
                      style={{ color: isArchived ? COLORS.subtle : COLORS.title, fontStyle: isArchived ? "italic" : "normal" }}
                    >
                      {s.title}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-[11px] font-body" style={{ color: COLORS.subtle }}>
                      {s.status && <StatusBadge status={s.status} />}
                      {s.child_count != null && <span>{s.child_count} {s.child_label}</span>}
                      {s.extra_count != null && <span>· {s.extra_count} {s.extra_label}</span>}
                      {s.item_type && <span className="uppercase tracking-wider">{s.item_type}</span>}
                      {s.updated_at && <span>· {new Date(s.updated_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = status === "published"
    ? { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" }
    : status === "archived"
    ? { bg: "#f3f4f6", fg: "#6b7280", border: "#e5e7eb" }
    : { bg: "#fffbeb", fg: "#b45309", border: "#fde68a" };
  return (
    <Badge
      className="text-[9px] border"
      style={{ backgroundColor: styles.bg, color: styles.fg, borderColor: styles.border }}
    >
      {status}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Screen 3
// ---------------------------------------------------------------------------
function Screen3({ tree, loading, type, checked, expanded, toggleNode, toggleExpand, isIndeterminate, selectAll, parentOnly, summaryLines }) {
  if (loading || !tree) {
    return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" style={{ color: COLORS.subtle }} /></div>;
  }

  const renderNodes = (node, depth, isRoot, out) => {
    const key = `${node.kind}:${node.id}`;
    const isChecked = checked.has(key);
    const hasChildren = (node.children || []).length > 0;
    const isOpen = expanded.has(key);
    const indet = isIndeterminate(node);
    out.push(
      <div
        key={key}
        className="flex items-center gap-2 py-1 pr-2 rounded transition-colors"
        style={{ paddingLeft: 8 + depth * 18 }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = COLORS.hover; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
        data-testid={`dup-tree-node-${node.id}`}
      >
        {hasChildren ? (
          <button type="button" onClick={() => toggleExpand(node)} className="shrink-0" style={{ color: COLORS.subtle }} data-testid={`dup-tree-expand-${node.id}`}>
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
        ) : (
          <span className="inline-block h-3.5 w-3.5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => !isRoot && toggleNode(node)}
          disabled={isRoot}
          data-testid={`dup-tree-check-${node.id}`}
          className="h-4 w-4 rounded shrink-0 flex items-center justify-center transition-colors"
          style={{
            border: `1.5px solid ${isChecked || indet ? COLORS.primary : COLORS.subtle}`,
            backgroundColor: isChecked ? COLORS.primary : indet ? `${COLORS.primary}80` : "transparent",
            cursor: isRoot ? "not-allowed" : "pointer",
            opacity: isRoot ? 0.6 : 1,
          }}
        >
          {indet && !isChecked ? <Minus className="h-3 w-3 text-white" /> : isChecked && <Check className="h-3 w-3 text-white" />}
        </button>
        <span className="text-[10px] font-body uppercase tracking-wider shrink-0" style={{ color: COLORS.subtle }}>
          {KIND_LABEL[node.kind] || node.kind}
        </span>
        <span className="text-sm font-body truncate flex-1" style={{ color: COLORS.title }} data-testid={`dup-tree-title-${node.id}`}>
          {node.title}
        </span>
        {node.meta && <span className="text-[10px] font-body shrink-0 uppercase tracking-wider" style={{ color: COLORS.subtle }}>{String(node.meta)}</span>}
      </div>
    );
    if (hasChildren && isOpen) {
      for (const c of node.children) renderNodes(c, depth + 1, false, out);
    }
  };
  const rendered = [];
  renderNodes(tree, 0, true, rendered);

  return (
    <div className="space-y-4" data-testid="dup-screen-3">
      <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>Select what to duplicate</h3>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={selectAll}
          className="h-8 font-body"
          style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.body }}
          data-testid="dup-shortcut-all"
        >
          All Items
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={parentOnly}
          className="h-8 font-body"
          style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.body }}
          data-testid="dup-shortcut-parent-only"
        >
          {TYPE_META[type].label} Only
        </Button>
      </div>
      <div
        className="rounded-lg max-h-[45vh] overflow-y-auto p-2"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}
      >
        {rendered}
      </div>
      <div className="text-xs font-body" style={{ color: COLORS.body }} data-testid="dup-summary-line">
        Duplicating {summaryLines.join(", ")}.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen 4
// ---------------------------------------------------------------------------
function Screen4({ type, source, counts, newTitle, setNewTitle }) {
  return (
    <div className="space-y-4" data-testid="dup-screen-4">
      <h3 className="text-lg font-heading font-bold" style={{ color: COLORS.title }}>Confirm duplication</h3>

      <div
        className="rounded-lg p-4 space-y-2"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}
      >
        <p className="text-xs font-body font-semibold uppercase tracking-wider" style={{ color: COLORS.subtle }}>
          You're about to create
        </p>
        {type === "item" ? (
          <p className="text-sm font-body" style={{ color: COLORS.title }}>• 1 Question Bank item</p>
        ) : counts.length === 0 ? (
          <p className="text-sm font-body" style={{ color: COLORS.title }}>• 1 {TYPE_META[type].label}</p>
        ) : (
          <ul className="text-sm font-body space-y-0.5" style={{ color: COLORS.title }}>
            {counts.map((c) => (<li key={c}>• {c}</li>))}
          </ul>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="font-body text-xs uppercase tracking-wider" style={{ color: COLORS.subtle }}>New title</Label>
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          data-testid="dup-new-title"
          className="h-10 text-sm font-body"
          style={{ backgroundColor: "#fff", borderColor: COLORS.border, color: COLORS.title }}
        />
        <p className="text-[11px] font-body" style={{ color: COLORS.subtle }}>
          Source: <span style={{ color: COLORS.body }}>{source?.title}</span>
        </p>
      </div>

      <div
        className="rounded-lg px-4 py-2.5"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface }}
      >
        <p className="text-xs font-body" style={{ color: COLORS.body }}>
          Duplicates will be added to your library as <span className="font-semibold" style={{ color: COLORS.title }}>Draft</span>.
        </p>
      </div>
    </div>
  );
}
